import * as React from 'react';
import { useHistory } from 'react-router';
import { Authority } from '../components/AuthorityLabel';
import { Button } from '../components/Button';
import { GraphQLErrorDialog } from '../components/Dialogs';
import { EmptyRow } from '../components/EmptyRow';
import { PageHeader } from '../components/PageHeader';
import {
  useGetCustomersLazyQuery,
  useUpdateCustomersDisplayOrderMutation,
} from '../generated/graphql';
import { Loading } from '../Loading';
import './CustomerListPage.css';
import { TableScroll } from '../components/TableScroll';
import { orderByIds } from '../utils/reorder';
import {
  getReorderTableStorageKey,
  getTableColumnCount,
  ReorderHandleCell,
  ReorderHandleHeader,
  useListReorder,
} from '../components/ReorderMode';
import { ListSummary } from '../components/ListControls';
import { LIST_SEARCH_EMPTY_MESSAGE, useListSearch } from '../components/ListSearch';

/**
 * 表の列数。0件の行を表の幅いっぱいに広げるために使う。
 * 並びを変えられる人には左端に取っ手の列が加わるので、0件の行の幅は getTableColumnCount で数える
 */
const COLUMN_COUNT = 2;

/**
 * お客様の編集に必要な権限の段階。
 * この一覧の編集は、管理（/admin配下）の画面を開ける管理者以上に出しており、
 * サーバーの並び順の保存（updateCustomersDisplayOrder）も管理者以上なので、「並び替え」も同じ段階にする
 */
const EDIT_AUTHORITY_LEVEL = Authority.Admin;

/**
 * お客様の一覧。お客様を削除する操作はAPIにないので、操作は編集だけを置く。
 */
export const CustomerListPage: React.FC = function () {
  const history = useHistory();
  const [getCustomers, getCustomersResult] = useGetCustomersLazyQuery();
  // 見出しの右の絞り込みの欄。絞り込んでいる間は並びを変えさせない
  const search = useListSearch();
  const [updateDisplayOrder] = useUpdateCustomersDisplayOrderMutation();
  // データを読み込む前は空の並びを渡し、読み込んだ後はサーバーが返した並び順を渡す。
  // 取っ手を落とすとすぐに保存し、保存の通知と「元に戻す」はこのフックが出す
  const reorderMode = useListReorder((getCustomersResult.data?.getCustomers ?? []).map((customer) => customer.id), {
    authLv: EDIT_AUTHORITY_LEVEL,
    filtering: search.filtering,
    save: (ids) => updateDisplayOrder({ variables: { customerIds: ids } }),
  });

  if (!getCustomersResult.called) {
    getCustomers();
  }

  if (getCustomersResult.error) {
    return <GraphQLErrorDialog error={getCustomersResult.error} />;
  }

  if (getCustomersResult.loading || !getCustomersResult.data) {
    return <Loading />;
  }

  const customers = getCustomersResult.data.getCustomers;
  const displayedCustomers = orderByIds(customers, reorderMode.displayedIds).filter((customer) =>
    search.matches([customer.name])
  );

  let rows;
  if (displayedCustomers.length === 0) {
    // 1件も登録されていないときと、絞り込みで1件も残らなかったときで、出す文を分ける
    let emptyMessage = LIST_SEARCH_EMPTY_MESSAGE;
    if (customers.length === 0) {
      emptyMessage = 'お客様が登録されていません';
    }
    rows = <EmptyRow colSpan={getTableColumnCount(reorderMode, COLUMN_COUNT)} message={emptyMessage} />;
  } else {
    rows = displayedCustomers.map((c, index) => {
      // 並びを変えられる人には、行の左端に取っ手の列を置く。操作の列（編集）はいつも出す
      let handleCell;
      if (reorderMode.handleColumn) {
        handleCell = <ReorderHandleCell {...reorderMode.getHandleCellProps(c.id, index)} />;
      }
      const actionCell = (
        <td className="App-table_button_cell">
          <div className="App-table_actions">
            <Button
              mini={true}
              color="white"
              onClick={() => history.push(`/admin/clients/${c.id}/edit`)}
            >
              編集
            </Button>
          </div>
        </td>
      );

      return (
        <tr key={c.id} {...reorderMode.getRowProps(c.id)}>
          {handleCell}
          <td>{c.name}</td>
          {actionCell}
        </tr>
      );
    });
  }

  // 見出しの右のボタン
  const headerActions = (
    <>
      <Button mini={true} onClick={() => history.push('/admin/clients/new')}>
        新規作成
      </Button>
    </>
  );

  // 並びを変えられる人には、取っ手の列を左端に置き、見出しは空にして幅を変えられないようにする
  let handleHeader;
  if (reorderMode.handleColumn) {
    handleHeader = <ReorderHandleHeader />;
  }
  const actionHeader = <th className="App-table_actions_column">操作</th>;

  const tableStorageKey = getReorderTableStorageKey(reorderMode, 'customers');

  return (
    <div className="CustomerListPage">
      <PageHeader
        title="お客様一覧"
        breadcrumbs={[{ label: '管理画面' }, { label: 'お客様一覧' }]}
        actions={headerActions}
      />
      <ListSummary
        reorder={reorderMode}
        shownCount={displayedCustomers.length}
        search={{ search, placeholder: '名前で絞り込み' }}
      />
      {/* 取っ手の列があるかどうかで列の並びが違うので、列の幅は別々に保存する */}
      <TableScroll key={tableStorageKey} storageKey={tableStorageKey}>
        <table className="App-table App-table_full">
          <thead>
            <tr>
              {handleHeader}
              <th>名前</th>
              {actionHeader}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </TableScroll>
    </div>
  );
};
